import React, { useLayoutEffect, useRef, useState } from 'react';

export interface MapObject { id: string; label: string; content: React.ReactNode; onOpen: () => void; onPrepare?: () => void }
type Camera = { x: number; y: number; scale: number };
const positions = new Map<string, string[]>();
const cameras = new Map<string, Camera>();
const bounded = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export function UniverseMap({ id, objects, regions = false }: { id: string; objects: MapObject[]; regions?: boolean }) {
  const viewport = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; camera: Camera; moved: boolean } | undefined>(undefined);
  const [size, setSize] = useState({ width: 1000, height: 560 });
  const [camera, setCamera] = useState<Camera>(cameras.get(id) || { x: 0, y: 0, scale: 1 });
  const slotIds = (positions.get(id) || []).filter(slot => objects.some(object => object.id === slot));
  objects.forEach(object => { if (!slotIds.includes(object.id)) slotIds.push(object.id); });
  positions.set(id, slotIds);
  const cellWidth = regions ? 420 : 340;
  const cellHeight = regions ? 330 : 460;
  const count = Math.max(1, slotIds.length);
  const capacity = Math.max(1, Math.min(4, Math.floor(size.width / cellWidth)));
  const rows = Math.ceil(count / capacity);
  const columns = Math.ceil(count / rows);
  const width = columns * cellWidth + 40;
  const height = rows * cellHeight + 60;
  const fit = Math.min(1.2, Math.max(1, size.width - 24) / width, Math.max(1, size.height - 50) / height);
  // Saved zoom is valid only for the same contents and viewport geometry.
  const cameraKey = `${id}:${slotIds.join(',')}:${size.width}:${size.height}`;
  const minimum = Math.max(.18, fit * .8);
  useLayoutEffect(() => {
    const node = viewport.current;
    if (!node) return;
    setSize({ width: node.clientWidth, height: node.clientHeight });
    const observer = new ResizeObserver(entries => setSize({ width: entries[0].contentRect.width, height: entries[0].contentRect.height }));
    observer.observe(node);
    return () => { observer.disconnect(); };
  }, []);
  useLayoutEffect(() => {
    const next = cameras.get(cameraKey) || { x: 0, y: 0, scale: fit };
    setCamera(next);
    // Initial sizing stays responsive until the user moves the camera.
  }, [cameraKey, fit]);
  const move = (next: Camera) => {
    const scale = bounded(next.scale, minimum, 2.2);
    const xLimit = Math.max(0, (width * scale - size.width) / 2 + 60);
    const yLimit = Math.max(0, (height * scale - size.height) / 2 + 60);
    const value = { scale, x: bounded(next.x, -xLimit, xLimit), y: bounded(next.y, -yLimit, yLimit) };
    setCamera(value); cameras.set(cameraKey, value);
  };
  const open = (object: MapObject) => {
    if (drag.current?.moved) return;
    cameras.set(cameraKey, camera);
    object.onOpen();
  };
  return <div className={`universe-map ${regions ? 'universe-map--regions' : ''}`}>
    <div className="universe-map__viewport" ref={viewport} aria-label={regions ? 'Regions map' : 'Communities map'} tabIndex={0}
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return;
        const delta = { ArrowLeft: [90, 0], ArrowRight: [-90, 0], ArrowUp: [0, 90], ArrowDown: [0, -90] }[event.key];
        if (delta) { event.preventDefault(); move({ ...camera, x: camera.x + delta[0], y: camera.y + delta[1] }); }
      }}
      onPointerDown={event => { if (event.button !== 0 || event.pointerType === 'touch') return; drag.current = { x: event.clientX, y: event.clientY, camera, moved: false }; }}
      onPointerMove={event => { const start = drag.current; if (!start || !event.buttons) return; const dx = event.clientX - start.x; const dy = event.clientY - start.y; if (Math.hypot(dx, dy) > 7) { start.moved = true; event.currentTarget.setPointerCapture(event.pointerId); move({ ...start.camera, x: start.camera.x + dx, y: start.camera.y + dy }); } }}
      onPointerUp={() => { setTimeout(() => { drag.current = undefined; }, 0); }} onPointerCancel={() => { drag.current = undefined; }}>
      <div className="universe-map__canvas" style={{ width, height, marginLeft: -width / 2, marginTop: -height / 2, transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.scale})` }}>
        {objects.map(object => {
          const index = slotIds.indexOf(object.id);
          const row = Math.floor(index / columns);
          const rowCount = Math.min(columns, slotIds.length - row * columns);
          const x = 20 + (columns - rowCount) * cellWidth / 2 + (index % columns + .5) * cellWidth;
          const y = 30 + (row + .5) * cellHeight;
          return <div role="link" tabIndex={0} key={object.id} aria-label={object.label} className="universe-map__object" style={{ left: x, top: y, width: cellWidth - 38 }}
            onPointerEnter={object.onPrepare}
            onFocus={() => { object.onPrepare?.(); if (!drag.current && camera.scale > fit * 1.15) move({ ...camera, x: (width / 2 - x) * camera.scale, y: (height / 2 - y) * camera.scale }); }}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(object); } }}
            onClick={() => open(object)}>{object.content}</div>;
        })}
      </div>
    </div>
    <div className="universe-map__controls" aria-label="Map controls"><button onClick={() => move({ ...camera, scale: camera.scale / 1.25 })} aria-label="Zoom out">−</button><button onClick={() => move({ ...camera, scale: camera.scale * 1.25 })} aria-label="Zoom in">+</button><button onClick={() => move({ x: 0, y: 0, scale: fit })}>{regions ? 'View all' : 'View entire region'}</button></div>
    {camera.scale > fit * 1.15 && <span className="universe-map__hint">↔ Drag to explore · or use arrow keys</span>}
  </div>;
}
